import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { map, catchError, timeout } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface PaynowPaymentRequest {
  amount: number;
  externalId: string;
  description: string;
  buyer: {
    email: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
  };
  continueUrl?: string;
  notifyUrl?: string;
}

export interface PaynowPaymentResponse {
  paymentId: string;
  status: string;
  redirectUrl?: string;
  orderToken?: string;
  externalId: string;
}

export interface FinalizedPaynowOrder {
  orderCreated: boolean;
  orderId?: number;
  orderReference?: string;
  isGuestOrder?: boolean;
  status: string;
}

export interface PaymentStatus {
  paymentId: string;
  status: 'NEW' | 'PENDING' | 'CONFIRMED' | 'REJECTED' | 'ERROR';
  amount?: number;
  currency?: string;
  externalId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class PaynowService {
  private readonly checkoutKeyStorage = 'brocom_paynow_checkout_key';
  
  private paymentStatusSubject = new BehaviorSubject<PaymentStatus | null>(null);
  public paymentStatus$ = this.paymentStatusSubject.asObservable();
  
  // Backend API base URL
  private readonly backendUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  /**
   * Process order payment through backend API
   */
  processOrderPayment(orderData: any): Observable<PaynowPaymentResponse> {
    const requestFingerprint = JSON.stringify(orderData);
    let checkoutKey: string;
    try {
      const saved = JSON.parse(
        sessionStorage.getItem(this.checkoutKeyStorage) || 'null',
      );
      checkoutKey =
        saved?.fingerprint === requestFingerprint && saved?.key
          ? saved.key
          : crypto.randomUUID();
      sessionStorage.setItem(
        this.checkoutKeyStorage,
        JSON.stringify({ fingerprint: requestFingerprint, key: checkoutKey }),
      );
    } catch {
      checkoutKey = crypto.randomUUID();
    }

    return this.http.post<PaynowPaymentResponse>(
      `${this.backendUrl}/payments/paynow/create`,
      { ...orderData, checkoutKey },
    ).pipe(
      timeout(30000), // 30 second timeout
      map((response: any) => {
        // Debug log removed
        
        // Update payment status
        this.paymentStatusSubject.next({
          paymentId: response.paymentId,
          status: response.status,
          amount: orderData.total,
          externalId: response.externalId
        });
        
        return response;
      }),
      catchError((error: any) => {
        // Error log removed
        
        // Handle timeout specifically
        if (error.name === 'TimeoutError') {
          return throwError({
            error: {
              message: 'Payment request timed out. Please try again.'
            },
            status: 408
          });
        }

        if (error.status === 410) {
          sessionStorage.removeItem(this.checkoutKeyStorage);
        }
        
        // Handle other network errors
        if (!error.status) {
          return throwError({
            error: {
              message: 'Network error. Please check your connection and try again.'
            },
            status: 500
          });
        }
        
        throw error;
      })
    );
  }

  finalizeOrderPayment(
    paymentId: string,
    orderData?: any,
    orderToken?: string,
  ): Observable<FinalizedPaynowOrder> {
    return this.http.post<FinalizedPaynowOrder>(
      `${this.backendUrl}/payments/paynow/finalize`,
      { paymentId, orderData, orderToken },
    );
  }

  clearCheckoutKey(): void {
    sessionStorage.removeItem(this.checkoutKeyStorage);
  }

  /**
   * Check payment status
   */
  getPaymentStatus(paymentId: string): Observable<PaymentStatus> {
    return this.http.get<PaymentStatus>(`${this.backendUrl}/payments/paynow/status/${paymentId}`);
  }

  /**
   * Get current payment status
   */
  getCurrentPaymentStatus(): PaymentStatus | null {
    return this.paymentStatusSubject.value;
  }

  /**
   * Clear payment status
   */
  clearPaymentStatus(): void {
    this.paymentStatusSubject.next(null);
  }
}
